/**
 * «Tall for dagen» under the day (owner 2026-10-27, prototype vaktplan-real.prototype.html «Dag»): the day's figures
 * on one line, the same rules as the week and the month (figures.ts), and behind «Diagram» people against need hour
 * by hour, open by default, as the hour by hour picture is what the day view is for.
 */
import type { ApiShift } from '@/api/data';
import type { CostDay } from '@/api/labour';
import { T } from '@/i18n';
import type { NeedProfile } from '@/shell/local';
import css from './DayFigures.module.css';
import { ChartsButton, FoldButton, MissLine, useNotUsed, useStored } from './FiguresBits';
import wf from './WeekFigures.module.css';
import { hourCover, hourRange } from './chartFigures';
import { dayNumbers, h1, kr, missing, p1, pt, verdict } from './figures';
import { type SalesDay, needHours } from './staffing';

const hh = (h: number) => String(h).padStart(2, '0');

export function DayFigures({
  date,
  today,
  need,
  target,
  targetSet,
  shifts,
  cost,
  sales,
  money,
  tenantId,
  onSales,
}: {
  date: string;
  today: string;
  need: NeedProfile;
  target: number;
  targetSet: boolean;
  shifts: ApiShift[];
  cost: Map<string, CostDay>;
  sales: Record<string, SalesDay>;
  /** may this reader see kroner (vaktplan.kost) */
  money: boolean;
  tenantId: number | undefined;
  /** the sales are typed in the week: «Omsetning» in «Mangler» goes there */
  onSales: () => void;
}) {
  const [open, setOpen] = useStored('vaktplan.day.open', true);
  const [charts, setCharts] = useStored('vaktplan.day.charts', true);
  const [notUsed, setNotUsed] = useNotUsed(tenantId);

  const needSet = needHours(need, date) != null;
  const needOn = needSet && !notUsed.includes('need');
  const salesUsed = money && !notUsed.includes('sales');
  const x = dayNumbers(date, { today, tmode: undefined, need, needOn, shifts, cost, sales });
  const anySales = x.f.sales != null;
  const share = salesUsed && x.f.sales ? x.f.share : null;
  const over = (p: number | null) => targetSet && !x.unpriced && p != null && p > target + 1;
  const miss = missing({ needSet, money, anySales, targetSet, unpriced: x.unpriced, notUsed });

  // the hours: «Dekket» counts the hours with enough people, the chart draws each of them
  const [h0, h1x] = hourRange(need, [date], shifts);
  const zs = hourCover(need, date, shifts, h0, h1x);
  const withNeed = zs.filter((z) => z.need > 0);
  const covered = withNeed.filter((z) => z.on + 1e-6 >= z.need).length;
  const coverTxt = needOn && withNeed.length > 0 && (
    <span>
      {T('Dekket')}{' '}
      <b className={covered < withNeed.length ? wf.warn : undefined}>
        {T('{a} av {b} timer', { a: covered, b: withNeed.length })}
      </b>
    </span>
  );
  const pctTxt = share != null && (
    <span>
      {T('Lønn %')}{' '}
      <b className={over(share) ? wf.warn : undefined}>
        {p1(share)}
        {x.unpriced ? '*' : ''}
      </b>
    </span>
  );
  const missLine = <MissLine miss={miss} onHide={(k) => setNotUsed([...notUsed, k])} onSales={onSales} />;

  return (
    <section className={css.wrap} data-testid="day-figures" aria-label={T('Tall for dagen')}>
      <div className={css.head}>
        <FoldButton open={open} onClick={() => setOpen(!open)} label={T('Tall for dagen')} />
        {!open && (
          <span className={css.line}>
            {coverTxt}
            {pctTxt || (
              <span>
                {T('Timer')} <b>{h1(x.hours)} t</b>
              </span>
            )}
          </span>
        )}
        {missLine}
        <span className={wf.sp} />
        {open && <ChartsButton open={charts} onClick={() => setCharts(!charts)} />}
      </div>
      {open && (
        <>
          <div className={css.figs}>
            {coverTxt}
            <span>
              {T('Timer')} <b>{h1(x.hours)} t</b>
            </span>
            {salesUsed && x.f.sales != null && (
              <span>
                {T('Omsetning')} <b>{kr(x.f.sales)} kr</b>
                {(x.est || !x.past) && (
                  <span className={wf.sub}> {x.est ? T('estimat') : T('prognose')}</span>
                )}
              </span>
            )}
            {money && (
              <span title={x.unpriced ? T('For lav: vakter uten timelønn') : undefined}>
                {T('Lønnskost')}{' '}
                <b>
                  {kr(x.f.cost)} kr{x.unpriced ? '*' : ''}
                </b>
              </span>
            )}
            {share != null && (
              <span>
                {T('Lønn %')} {targetSet && <span className={wf.sub}>{T('mål {p}', { p: pt(target) })}</span>}{' '}
                <b className={over(share) ? wf.warn : undefined}>{p1(share)}</b>
                {targetSet && !x.unpriced && x.f.sales ? (
                  <span className={wf.sub}> {verdict(x.f.cost, x.f.sales, target)}</span>
                ) : null}
              </span>
            )}
          </div>
          {/* ponytail: sales per hour are not stored, so «Omsetning» per hour waits for a till that sends them */}
          {charts && zs.length > 0 && <HoursChart zs={zs} needOn={needOn} />}
        </>
      )}
    </section>
  );
}

/** people against need, hour by hour: need filled, people on shift as a line, short orange, one too many blue */
function HoursChart({ zs, needOn }: { zs: ReturnType<typeof hourCover>; needOn: boolean }) {
  const W = 1000;
  const H = 120;
  const n = zs.length;
  const top = Math.max(4, ...zs.map((z) => Math.max(needOn ? z.need : 0, z.on)));
  const X = (k: number) => (k / n) * W;
  const Y = (v: number) => H - (v / top) * (H - 8);
  const step = (get: (z: (typeof zs)[number]) => number) =>
    zs.map((z, k) => `${k ? 'L' : 'M'}${X(k)} ${Y(get(z))}H${X(k + 1)}`).join('');
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += top > 6 ? 2 : 1) ticks.push(v);
  const short = needOn ? zs.filter((z) => z.on + 1e-6 < z.need) : [];
  const many = needOn ? zs.filter((z) => z.on - z.need >= 1) : [];
  return (
    <div className={css.chart}>
      <div className={css.key}>
        <b>{T('Timer')}</b>
        {needOn && (
          <span>
            <i className={css.kNeed} />
            {T('Behov')}
          </span>
        )}
        <span>
          <i className={css.kOn} />
          {T('På jobb')}
        </span>
        {short.length > 0 && (
          <span>
            <i className={css.kShort} />
            {T('Mangler folk')}
          </span>
        )}
        {many.length > 0 && (
          <span>
            <i className={css.kMany} />
            {T('For mange')}
          </span>
        )}
      </div>
      <div className={css.plot}>
        <div className={css.ya} aria-hidden="true">
          {ticks.map((v) => (
            <span key={v} style={{ top: `${(Y(v) / H) * 100}%` }}>
              {v}
            </span>
          ))}
        </div>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={T('Personer per time mot behov')}
        >
          {ticks.map((v) => (
            <line key={v} x1={0} x2={W} y1={Y(v)} y2={Y(v)} className={css.grid} />
          ))}
          {needOn && <path className={css.need} d={`${step((z) => z.need)}V${H}H0Z`} />}
          {short.map((z) => {
            const k = zs.indexOf(z);
            return (
              <rect
                key={`s${z.h}`}
                className={css.short}
                x={X(k)}
                y={Y(z.need)}
                width={W / n}
                height={Y(z.on) - Y(z.need)}
              />
            );
          })}
          {many.map((z) => {
            const k = zs.indexOf(z);
            return (
              <rect
                key={`m${z.h}`}
                className={css.many}
                x={X(k)}
                y={Y(z.on)}
                width={W / n}
                height={Y(z.need) - Y(z.on)}
              />
            );
          })}
          <path className={css.on} d={step((z) => z.on)} />
          {zs.map((z, k) => (
            <rect key={`h${z.h}`} className={css.hit} x={X(k)} y={0} width={W / n} height={H}>
              <title>
                {`kl. ${hh(z.h)}-${hh(z.h + 1)}: ${T('på jobb')} ${h1(z.on)}${needOn ? `, ${T('behov')} ${h1(z.need)}` : ''}`}
              </title>
            </rect>
          ))}
        </svg>
        <div className={css.axis} aria-hidden="true">
          {zs
            .filter((_, k) => k % 2 === 0)
            .map((z) => (
              <span key={z.h} style={{ left: `${(zs.indexOf(z) / n) * 100}%` }}>
                {hh(z.h)}
              </span>
            ))}
        </div>
      </div>
    </div>
  );
}
